import morphologyData from './morphology-data.json'

/**
 * Морфология названий "поста/кабинета" для universal LK.
 *
 * Где данные (все возможные типы названий для рабочего места):
 * - `front/utils/morphology-data.json`
 * - `words` — словарь форм по каждому слову.
 *
 * Термин по умолчанию (и его падежи) лежит не здесь, а в локалях:
 * `interface.workplace.terms.*` — см. `front/utils/workplace.js`.
 *
 * Как добавить новое слово:
 * 1) Откройте `words` в `morphology-data.json`.
 * 2) Добавьте запись одним из двух способов:
 *    - По шаблону (быстро):
 *      `"Кабинет": { "pattern": "masc_hard", "token": "кабинет" }`
 *    - Вручную (если нестандартное слово):
 *      `"X": { "cases": {...}, "plural": "...", "pluralGenitive": "..." }`
 * 3) Если шаблон дал не ту форму, добавьте override:
 *    `plural` и/или `pluralGenitive`.
 *
 * Расшифровка `cases`:
 * - nominative: именительный (кто? что?) -> "Кабинет"
 * - genitive: родительный (кого? чего?) -> "Кабинета"
 * - dative: дательный (кому? чему?) -> "Кабинету"
 * - accusative: винительный (кого? что?) -> "Кабинет"
 * - instrumental: творительный (кем? чем?) -> "Кабинетом"
 * - prepositional: предложный (о ком? о чем?) -> "Кабинете"
 *
 * Расшифровка полей записи в `words`:
 * - pattern: шаблон генерации форм.
 *   Типы:
 *   - masc_hard: мужской род
 *     (Кабинет, Пост, Зал, Бокс, Центр).
 *   - fem_a: женский род на "-а"
 *     (Комната, Клиника, Зона, Секция, Мойка).
 *   - fem_ya: женский род на "-я"
 *     (Студия, Станция, Лаборатория).
 *   - fem_aya: женский род на "-ая"
 *     (Мастерская).
 *   - fem_nya: женский род на "-ня" с отдельными формами
 *     (Кухня).
 * - token: нормализованная форма слова (обычно lowercase), служебное поле.
 * - plural: множественное число (override), пример: "Кабинеты".
 * - pluralGenitive: родительный множественного числа (override), пример: "Кабинетов".
 *
 * Важно:
 * - При старте запускается валидация `validateMorphologyData()`.
 * - Если для слова не хватает форм, приложение бросит ошибку и попросит добавить формы.
 */

const WORDS = morphologyData.words || {}
const CASES = [
  'nominative',
  'genitive',
  'dative',
  'accusative',
  'instrumental',
  'prepositional',
]

const endsWithSibilant = (text) => /[гкхжчшщц]$/i.test(text)
const lowerFirst = (text) =>
  text && text.length > 0 ? text.charAt(0).toLowerCase() + text.slice(1) : text

/**
 * Шаблоны генерации форм для типовых слов.
 * Каждый шаблон возвращает:
 * - cases: набор падежей
 * - plural: множественное число
 * - pluralGenitive: родительный падеж множественного числа
 */
const patterns = {
  masc_hard(word) {
    const pluralSuffix = endsWithSibilant(word) ? 'и' : 'ы'
    return {
      cases: {
        nominative: word,
        genitive: `${word}а`,
        dative: `${word}у`,
        accusative: word,
        instrumental: `${word}ом`,
        prepositional: `${word}е`,
      },
      plural: `${word}${pluralSuffix}`,
      pluralGenitive: `${word}ов`,
    }
  },
  fem_a(word) {
    const base = word.slice(0, -1)
    const yi = endsWithSibilant(base) ? 'и' : 'ы'
    return {
      cases: {
        nominative: word,
        genitive: `${base}${yi}`,
        dative: `${base}е`,
        accusative: `${base}у`,
        instrumental: `${base}ой`,
        prepositional: `${base}е`,
      },
      plural: `${base}${yi}`,
      pluralGenitive: base,
    }
  },
  fem_ya(word) {
    const base = word.slice(0, -1)
    return {
      cases: {
        nominative: word,
        genitive: `${base}и`,
        dative: `${base}и`,
        accusative: `${base}ю`,
        instrumental: `${base}ей`,
        prepositional: `${base}и`,
      },
      plural: `${base}и`,
      pluralGenitive: `${base}й`,
    }
  },
  fem_aya(word) {
    const base = word.slice(0, -2)
    return {
      cases: {
        nominative: word,
        genitive: `${base}ой`,
        dative: `${base}ой`,
        accusative: `${base}ую`,
        instrumental: `${base}ой`,
        prepositional: `${base}ой`,
      },
      plural: `${base}ие`,
      pluralGenitive: `${base}их`,
    }
  },
  fem_nya(word) {
    const base = word.slice(0, -1)
    return {
      cases: {
        nominative: word,
        genitive: `${base}и`,
        dative: `${base}е`,
        accusative: `${base}ю`,
        instrumental: `${base}ей`,
        prepositional: `${base}е`,
      },
      plural: `${base}и`,
      pluralGenitive: `${base}ь`,
    }
  },
}

/**
 * Строит полный набор форм слова из словаря:
 * - генерирует по pattern (если он задан),
 * - накладывает ручные overrides из JSON,
 * - добавляет fallback для plural/pluralGenitive.
 * @param {string} word
 * @returns {{token: string, cases: Object, plural: string, pluralGenitive: string} | null}
 */
function resolveWordForms(word) {
  const entry = WORDS[word]
  if (!entry) return null

  const generated =
    entry.pattern && patterns[entry.pattern]
      ? patterns[entry.pattern](word)
      : { cases: {}, plural: null, pluralGenitive: null }

  return {
    token: entry.token || lowerFirst(word),
    cases: { ...generated.cases, ...(entry.cases || {}) },
    plural: entry.plural || generated.plural || null,
    pluralGenitive: entry.pluralGenitive || generated.pluralGenitive || null,
  }
}

/**
 * Проверяет корректность morphology-data:
 * - у каждого слова должны быть все падежи из CASES,
 * - должны быть plural и pluralGenitive.
 * При ошибке бросает исключение при инициализации модуля.
 */
function validateMorphologyData() {
  const errors = []

  Object.keys(WORDS).forEach((word) => {
    const forms = resolveWordForms(word)
    if (!forms) {
      errors.push(`Нет форм для "${word}"`)
      return
    }

    CASES.forEach((caseName) => {
      if (!forms.cases?.[caseName]) {
        errors.push(`Для "${word}" не задан падеж "${caseName}"`)
      }
    })

    if (!forms.plural) {
      errors.push(`Для "${word}" не задано множественное число`)
    }
    if (!forms.pluralGenitive) {
      errors.push(
        `Для "${word}" не задан родительный падеж множественного числа`
      )
    }
  })

  if (errors.length > 0) {
    throw new Error(`Invalid morphology data:\n${errors.join('\n')}`)
  }
}

validateMorphologyData()

/**
 * Род названия места: m, f или n. Нужен для «Любой кабинет» / «Любая комната».
 * @param {string} word
 * @returns {'m'|'f'|'n'}
 */
export function getPlaceGender(word) {
  const entry = WORDS[word]
  if (entry?.pattern === 'masc_hard') return 'm'
  if (entry?.pattern?.startsWith('fem')) return 'f'
  return 'n'
}

/**
 * Возвращает форму слова в нужном падеже.
 * Слова нет в словаре — возвращает null: вызывающая сторона решает,
 * что показать (например, форму термина из локалей).
 * @param {string} word
 * @param {'nominative'|'genitive'|'dative'|'accusative'|'instrumental'|'prepositional'} [caseType='nominative']
 * @param {boolean} [capitalize=true] - если false, делает первую букву строчной
 * @returns {string|null}
 */
export function getDeclension(
  word,
  caseType = 'nominative',
  capitalize = true
) {
  const forms = resolveWordForms(word)
  let result = forms?.cases?.[caseType]

  if (!result) return null

  // Применяем капитализацию в зависимости от параметра
  if (!capitalize && result.length > 0) {
    result = lowerFirst(result)
  }

  return result
}

/**
 * Возвращает множественное число для слова.
 * @param {string} word
 * @returns {string|null}
 */
export function getPlural(word) {
  return resolveWordForms(word)?.plural || null
}

/**
 * Возвращает родительный падеж множественного числа.
 * Нужен для фраз вида: "Выбрано N кабинетов/постов".
 * @param {string} word
 * @returns {string|null}
 */
export function getPluralGenitive(word) {
  return resolveWordForms(word)?.pluralGenitive || null
}

// Экспортируем основные функции
export default {
  getDeclension,
  getPlural,
  getPluralGenitive,
}
